// Admin-console data access: the listUsers / setUserClaim / createUser callables.
//
// Every one of these is admin-guarded server-side. Nothing here is a security
// boundary — the UI hides what it can, but the function is what enforces.

import { httpsCallable, type FunctionsError } from 'firebase/functions'
import { ref } from 'vue'

import { functions } from '../firebase'
import type {
  CreateUserRequest,
  CreateUserResponse,
  ListUsersRequest,
  ListUsersResponse,
  SetUserClaimRequest,
  SetUserClaimResponse,
  UserRow,
} from '../types/callables'

const callList   = httpsCallable<ListUsersRequest, ListUsersResponse>(functions, 'listUsers')
const callSet    = httpsCallable<SetUserClaimRequest, SetUserClaimResponse>(functions, 'setUserClaim')
const callCreate = httpsCallable<CreateUserRequest, CreateUserResponse>(functions, 'createUser')

/** Turns a callable error into something worth showing a person. */
export function adminErrorMessage(e: unknown): string {
  const err  = e as FunctionsError
  const msg  = err?.message ?? ''
  const code = err?.code ?? ''

  if (msg.includes('email-already-exists')) return 'That email already has an account.'
  if (msg.includes('password-too-short'))   return 'Password must be at least 12 characters.'
  if (msg.includes('invalid-email'))        return 'That email address is not valid.'
  if (msg.includes('cannot-deactivate-self')) return 'You cannot deactivate your own account.'
  if (msg.includes('cannot-demote-self'))     return 'You cannot remove your own admin access.'
  if (msg.includes('user-not-found'))       return 'That user no longer exists. Refresh the list.'
  if (msg === 'default-not-in-profiles')    return 'Default profile must be one of the assigned profiles.'
  if (msg === 'profiles-shape')             return 'Assigned profiles list is malformed. Reload and try again.'
  if (msg === 'defaultProfile-shape')       return 'Default profile is malformed. Reload and try again.'
  if (code === 'functions/permission-denied') return 'You do not have permission to do that.'
  if (code === 'functions/unavailable')       return 'Could not reach the server. Try again.'
  return 'Something went wrong. Try again.'
}

export function useAdmin() {
  const users     = ref<UserRow[]>([])
  const loading   = ref(false)
  const error     = ref<string | null>(null)
  const nextToken = ref<string | undefined>(undefined)

  async function load(reset = true): Promise<void> {
    loading.value = true
    error.value = null
    try {
      // Omit the key entirely rather than passing undefined: the callable
      // protocol serializes an explicit undefined as null, which admin.listUsers
      // rejects outright instead of reading as "first page".
      const { data } = await callList(reset || !nextToken.value ? {} : { pageToken: nextToken.value })
      users.value = reset ? data.users : [...users.value, ...data.users]
      nextToken.value = data.nextPageToken
    } catch (e) {
      error.value = adminErrorMessage(e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Applies a claim change optimistically and rolls back if the server refuses.
   *
   * Optimistic because a toggle that visibly lags a round-trip invites the admin
   * to click it twice. The rollback captures every field the patch touches so
   * a server-side rejection (e.g. `default-not-in-profiles`) restores the row
   * exactly, not partway.
   */
  async function setClaim(uid: string, patch: Omit<SetUserClaimRequest, 'uid'>): Promise<boolean> {
    const row = users.value.find((u) => u.uid === uid)
    if (!row) return false

    const before = {
      active:         row.active,
      tier:           row.tier,
      role:           row.role,
      notes:          row.notes,
      profiles:       [...row.profiles],
      defaultProfile: row.defaultProfile,
    }
    if (patch.active         !== undefined) row.active         = patch.active
    if (patch.tier           !== undefined) row.tier           = patch.tier
    if (patch.role           !== undefined) row.role           = patch.role
    if (patch.notes          !== undefined) row.notes          = patch.notes ?? ''
    if (patch.profiles       !== undefined) row.profiles       = [...patch.profiles]
    if (patch.defaultProfile !== undefined) row.defaultProfile = patch.defaultProfile

    error.value = null
    try {
      await callSet({ uid, ...patch })
      return true
    } catch (e) {
      Object.assign(row, before)
      error.value = adminErrorMessage(e)
      return false
    }
  }

  async function createUser(req: CreateUserRequest): Promise<boolean> {
    error.value = null
    try {
      await callCreate(req)
      await load(true)
      return true
    } catch (e) {
      error.value = adminErrorMessage(e)
      return false
    }
  }

  return { users, loading, error, nextToken, load, setClaim, createUser }
}
